import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  GlobalStyles,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  BatteryChargingFull,
  ArrowForward,
} from "@mui/icons-material";
import { Auth } from "aws-amplify";

const s = {
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fff",
      transition: "all 0.3s ease",
      "&:hover fieldset": { borderColor: "#008b8b" },
      "&.Mui-focused fieldset": {
        borderColor: "#008b8b",
        boxShadow: "0 0 0 3px rgba(0, 139, 139, 0.1)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#666",
      "&.Mui-focused": { color: "#008b8b" },
    },
  },
  primaryButton: {
    background: "linear-gradient(135deg, #008b8b 0%, #20b2aa 100%)",
    padding: "10px",
    marginTop: "10px",
    borderRadius: "10px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    color: "#fff",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "rgba(255, 255, 255, 0.2)",
      transition: "left 0.5s ease",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #006b6b 0%, #1a9a99 100%)",
      boxShadow: "0 12px 32px rgba(0, 139, 139, 0.3)",
      transform: "translateY(-2px)",
      "&:before": { left: "100%" },
    },
    "&:disabled": {
      background: "#ccc",
      transform: "none",
    },
  },
  alertError: {
    mb: 3,
    background: "rgba(244, 67, 54, 0.1)",
    border: "1px solid rgba(244, 67, 54, 0.3)",
    color: "#c62828",
    borderRadius: "8px",
  },
  alertSuccess: {
    mb: 3,
    background: "rgba(76, 175, 80, 0.1)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
    color: "#2e7d32",
    borderRadius: "8px",
  },
};

const anim = (delay) => ({
  animation: `slideDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay} both`,
  willChange: "opacity, transform",
});

const PageLayout = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 2,
      position: "relative",
      overflow: "hidden",
    }}
  >
    <GlobalStyles
      styles={{
        "@keyframes slideUp": {
          "0%": { opacity: 0, transform: "translateY(40px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes slideDown": {
          "0%": { opacity: 0, transform: "translateY(-20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(50px)" },
        },
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: "-10%",
        right: "-5%",
        width: 250,
        height: 400,
        background:
          "radial-gradient(circle, rgba(0, 139, 139, 0.08) 0%, transparent 70%)",
        borderRadius: "50%",
        animation: "float 15s ease-in-out infinite",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: "-10%",
        left: "-5%",
        width: 250,
        height: 300,
        background:
          "radial-gradient(circle, rgba(32, 178, 170, 0.08) 0%, transparent 70%)",
        borderRadius: "50%",
        animation: "float 20s ease-in-out infinite reverse",
      }}
    />
    <Container maxWidth="xs" sx={{ position: "relative", zIndex: 2 }}>
      <Card
        elevation={2}
        sx={{
          padding: { xs: "32px 20px", sm: "48px 32px" },
          borderRadius: "16px",
          border: "1px solid rgba(0, 128, 128, 0.1)",
          background:
            "linear-gradient(135deg, #fff 0%, rgba(232, 244, 248, 0.3) 100%)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 32px rgba(0, 128, 128, 0.08)",
          animation: "slideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        {children}
      </Card>
    </Container>
  </Box>
);

const CardHeader = ({ title, subtitle }) => (
  <Box sx={{ textAlign: "center", mb: 4 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
        ...anim("0.2s"),
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "12px",
          background: "linear-gradient(135deg, #008b8b 0%, #20b2aa 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 24px rgba(0, 139, 139, 0.25)",
        }}
      >
        <BatteryChargingFull sx={{ fontSize: 32, color: "white" }} />
      </Box>
    </Box>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 700,
        color: "#1a1a1a",
        mb: 1,
        fontSize: { xs: "1.5rem", sm: "1.75rem" },
        ...anim("0.3s"),
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "#666", fontSize: "0.95rem", ...anim("0.4s") }}
    >
      {subtitle}
    </Typography>
  </Box>
);

const CardFooter = () => (
  <Box
    sx={{
      textAlign: "center",
      mt: 4,
      pt: 3,
      borderTop: "1px solid rgba(0, 128, 128, 0.1)",
      ...anim("0.8s"),
    }}
  >
    <Typography variant="caption" sx={{ color: "#999", fontSize: "0.85rem" }}>
      Trusted by battery swap operators across India
    </Typography>
  </Box>
);

const PasswordField = ({
  label,
  value,
  onChange,
  show,
  onToggleShow,
  disabled,
  delay,
  mb,
}) => (
  <Box sx={anim(delay)}>
    <TextField
      fullWidth
      label={label}
      type={show ? "text" : "password"}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={onToggleShow}
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
              disabled={disabled}
              sx={{ color: "#008b8b" }}
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ ...s.textField, mb }}
    />
  </Box>
);

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const [newPasswordMode, setNewPasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [cognitoUser, setCognitoUser] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await Auth.signIn(email, password);
      if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
        setCognitoUser(result);
        setNewPasswordMode(true);
      } else {
        window.location.reload();
      }
    } catch (err) {
      if (err.code === "UserNotConfirmedException") {
        setError("Please confirm your email before signing in.");
      } else if (err.code === "NotAuthorizedException") {
        setError("Invalid email or password.");
      } else if (err.code === "UserNotFoundException") {
        setError("User does not exist.");
      } else {
        setError(err.message || "Failed to sign in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await Auth.completeNewPassword(cognitoUser, newPassword);
      window.location.reload();
    } catch (err) {
      setError(err.message || "Failed to set new password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await Auth.forgotPassword(resetEmail);
      setResetSent(true);
    } catch (err) {
      setError(
        err.message ||
          "Failed to send reset email. Please check your email and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (newPasswordMode) {
    return (
      <PageLayout>
        <CardHeader
          title="Set New Password"
          subtitle="Your account requires a new password before you can continue."
        />
        <form onSubmit={handleSetNewPassword}>
          {error && (
            <Alert severity="error" sx={s.alertError}>
              {error}
            </Alert>
          )}
          <PasswordField
            key="new-password"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            show={showNewPassword}
            onToggleShow={() => setShowNewPassword(!showNewPassword)}
            disabled={loading}
            delay="0.5s"
            mb={2}
          />
          <PasswordField
            key="confirm-password"
            label="Confirm New Password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            show={showConfirmPassword}
            onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
            disabled={loading}
            delay="0.6s"
            mb={1}
          />
          <Box sx={{ mt: 2, ...anim("0.7s") }}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !newPassword || !confirmNewPassword}
              endIcon={loading ? undefined : <ArrowForward />}
              sx={s.primaryButton}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Set Password & Sign In"
              )}
            </Button>
          </Box>
        </form>
        <CardFooter />
      </PageLayout>
    );
  }

  if (resetMode) {
    return (
      <PageLayout>
        <CardHeader
          title="Reset Password"
          subtitle="Enter your email to receive password reset instructions."
        />
        <form onSubmit={handlePasswordReset}>
          {resetSent && (
            <Alert severity="success" sx={s.alertSuccess}>
              Check your email for password reset instructions!
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={s.alertError}>
              {error}
            </Alert>
          )}
          <Box sx={anim("0.5s")}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={loading || resetSent}
              sx={{ ...s.textField, mb: 1 }}
            />
          </Box>
          <Box sx={{ mt: 2, ...anim("0.6s") }}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || resetSent || !resetEmail}
              endIcon={loading ? undefined : <ArrowForward />}
              sx={{ ...s.primaryButton, mb: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Send Reset Email"
              )}
            </Button>
          </Box>
          <Box sx={anim("0.7s")}>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setResetMode(false);
                setError("");
                setResetSent(false);
                setResetEmail("");
              }}
              sx={{
                color: "#008b8b",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.95rem",
              }}
            >
              Back to Login
            </Button>
          </Box>
        </form>
        <CardFooter />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <CardHeader
        title="Welcome Back"
        subtitle="Sign in to manage your battery operations"
      />
      <form onSubmit={handleSignIn}>
        {error && (
          <Alert severity="error" sx={s.alertError}>
            {error}
          </Alert>
        )}
        <Box sx={anim("0.5s")}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
            sx={{ ...s.textField, mb: 2 }}
          />
        </Box>
        <PasswordField
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          show={showPassword}
          onToggleShow={() => setShowPassword(!showPassword)}
          disabled={loading}
          delay="0.6s"
          mb={1}
        />
        {/* <Box sx={{ textAlign: "right", mb: 3, ...anim("0.65s") }}>
          <Link
            onClick={() => {
              setResetMode(true);
              setError("");
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#008b8b",
              cursor: "pointer",
              textDecoration: "none",
              fontWeight: 500,
              transition: "all 0.2s ease",
              "&:hover": { textDecoration: "underline", color: "#20b2aa" },
            }}
          >
            Forgot password?
          </Link>
        </Box> */}
        <Box sx={anim("0.7s")}>
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading || !email || !password}
            endIcon={loading ? undefined : <ArrowForward />}
            sx={s.primaryButton}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Sign In"
            )}
          </Button>
        </Box>
      </form>
      <CardFooter />
    </PageLayout>
  );
};

export default LoginPage;
